import { cva, type VariantProps } from 'class-variance-authority';
import type { ComponentProps } from 'react';

import Image from '@vs-shop/image-link/Image';
import { Button, type ButtonProps } from '@vs-shop/ui/button';
import { clsxm } from '@vs-shop/utils';

import type { FramedMediaProps } from '../../internal/framedMedia.tsx';
import { RenderedPart, type RenderElement } from '../../internal/renderedPart.tsx';

/**
 * The person who answers, offered as a card:
 *
 *   <SupportCard variant="compact" ground="base" edge="ringed" hang="seated">
 *     <SupportCardAvatar
 *       variant="compact"
 *       surround="unframed"
 *       presence="online"
 *     >
 *       <SupportCardPhoto src={photo} alt={name} width={64} height={64} />
 *       <SupportCardPresence variant="pinned" presence="online" />
 *     </SupportCardAvatar>
 *     …
 *     <SupportCardName>{name}</SupportCardName>
 *     <SupportCardHours variant="compact">{hours}</SupportCardHours>
 *     <SupportCardContact href={`tel:${telValue}`} title={label}>
 *       <IconPhone />
 *       <SupportCardContactText variant="compact">{telReadable}</SupportCardContactText>
 *     </SupportCardContact>
 *   </SupportCard>
 *
 * **Two appearances, one anatomy.** A listing rail and a thank-you column carry
 * the `large` card — a column with the photograph hanging over its top edge, a
 * name, the ways to reach it and a chat control under them. A product panel and
 * a checkout step carry the `compact` one — a row, the photograph at its head
 * with the presence dot pinned to it. One set of parts serves both, and the
 * arms are the whole difference between them.
 *
 * **What stands in the card, and where, is the composition's.** The centring of
 * the large card's photograph, the container query the compact one's row turns
 * on, the margins between the large card's lines — all of it is layout between
 * parts, and it is written where the page writes it. What an arm decides is how
 * one part paints its own box.
 *
 * **Nothing here reads a contact record.** The name, the photograph's URL, the
 * telephone number, the hours and every label enter as children and props, so a
 * page showing one advisor in the checkout and another beside a category rail
 * is two compositions rather than a prop on the section.
 *
 * Server-first: every part is a plain component, and the two live signals — is
 * the advisor within business hours, is the chat manned — reach it as arms the
 * tenant picks.
 */

/* ── the card ─────────────────────────────────────────────────────────── */

/**
 * The card's own box: its shape, the ground it sits on, whether it is drawn
 * against that ground with a hairline, and whether it reserves room above
 * itself for a photograph that hangs over its edge.
 *
 * `ground` is separate from `variant` because the two do not move together: the
 * same large card is plain on a page whose ground is already intense, and
 * intense on a page whose ground is plain.
 *
 * Every axis names its empty appearance rather than leaving it to a sentinel:
 * `plain` and `seated` paint nothing, and are in the list so that a reader of
 * the recipe sees every appearance a card can have without inferring one from
 * an absent entry.
 */
const supportCardVariants = cva('', {
  variants: {
    variant: {
      /** A column: photograph over the top edge, the ways to reach it, a chat control. */
      large: 'flex flex-col justify-between rounded-xl px-5 py-5.5',
      /** A row: photograph at the head, everything else beside it. */
      compact: 'flex gap-5 rounded-xl p-8',
    },
    ground: {
      /** The page's plain ground. */
      base: 'bg-background',
      /** The page's weighted ground, for a card that has to lift off a plain one. */
      intense: 'bg-background-intense',
    },
    edge: {
      /** Drawn against its ground with a hairline, for a card that shares it. */
      ringed: 'ring-foreground/10 ring',
      /** No hairline: the ground under the card is enough to separate it. */
      plain: '',
    },
    hang: {
      /** Room above the card for a photograph that hangs over its top edge. */
      raised: 'mt-[55px]',
      /** No room reserved: nothing of the card's stands above it. */
      seated: '',
    },
  },
});

type SupportCardVariantName = NonNullable<VariantProps<typeof supportCardVariants>['variant']>;
type SupportCardGroundName = NonNullable<VariantProps<typeof supportCardVariants>['ground']>;
type SupportCardEdgeName = NonNullable<VariantProps<typeof supportCardVariants>['edge']>;
type SupportCardHangName = NonNullable<VariantProps<typeof supportCardVariants>['hang']>;

export type SupportCardVariant = SupportCardVariantName;
export type SupportCardGround = SupportCardGroundName;
export type SupportCardEdge = SupportCardEdgeName;
export type SupportCardHang = SupportCardHangName;

/**
 * The card's shape, bound to the recipe by `satisfies` so the two cannot drift.
 *
 * Every axis below follows the same pattern. The `satisfies` clause names the
 * PRIVATE alias rather than the exported one: an exported alias sharing its
 * identifier with the const it annotates is a cycle Storybook's react-docgen
 * follows until its stack runs out.
 */
export const SupportCardVariant = {
  /** A column. */
  LARGE: 'large',
  /** A row. */
  COMPACT: 'compact',
} as const satisfies Record<string, SupportCardVariantName>;

/** The ground the card sits on. */
export const SupportCardGround = {
  /** The plain ground. */
  BASE: 'base',
  /** The weighted ground. */
  INTENSE: 'intense',
} as const satisfies Record<string, SupportCardGroundName>;

/** Whether the card is drawn against its ground. */
export const SupportCardEdge = {
  /** A hairline around it. */
  RINGED: 'ringed',
  /** Nothing around it. */
  PLAIN: 'plain',
} as const satisfies Record<string, SupportCardEdgeName>;

/** Whether the card reserves room for a photograph over its top edge. */
export const SupportCardHang = {
  /** Room above it. */
  RAISED: 'raised',
  /** No room above it. */
  SEATED: 'seated',
} as const satisfies Record<string, SupportCardHangName>;

/** Required and closed — not `VariantProps`, which makes every axis optional. */
export type SupportCardVariantProps = {
  readonly variant: SupportCardVariant;
  readonly ground: SupportCardGround;
  readonly edge: SupportCardEdge;
  readonly hang: SupportCardHang;
};

export type SupportCardProps = ComponentProps<'div'> & SupportCardVariantProps;

/** The card. */
export const SupportCard = ({
  className,
  variant,
  ground,
  edge,
  hang,
  ...props
}: SupportCardProps) => (
  <div
    data-slot="support-card"
    className={clsxm(supportCardVariants({ variant, ground, edge, hang }), className)}
    {...props}
  />
);

/* ── the advisor's photograph ─────────────────────────────────────────── */

/**
 * The frame the photograph fills, and the tint behind it.
 *
 * `surround` is the ground BEHIND the card, not the card's own: the frame's
 * thick border is what makes the photograph read as cut out of the page it
 * hangs over, so it paints what is behind rather than what is under. A card
 * whose photograph sits inside it is `unframed` and gets no border at all.
 *
 * The tint under the photograph carries the advisor's presence, so a frame
 * whose image has not arrived still says whether anyone is there.
 */
const supportCardAvatarVariants = cva('', {
  variants: {
    variant: {
      /** The large card's: the size it hangs at. */
      large: 'size-[100px] rounded-full border-8',
      /** The compact card's: the size it sits at, and the frame its dot pins to. */
      compact: 'relative size-16 rounded-full',
    },
    surround: {
      /** The plain ground stands behind the card. */
      base: 'border-background',
      /** The weighted ground stands behind the card. */
      intense: 'border-background-intense',
      /** No border at all, for a frame that sits inside the card. */
      unframed: '',
    },
    presence: {
      /** Within business hours. */
      online: 'bg-support-online-image',
      /** Outside them. */
      offline: 'bg-support-offline-image',
    },
  },
});

type SupportCardAvatarVariantName = NonNullable<
  VariantProps<typeof supportCardAvatarVariants>['variant']
>;
type SupportCardSurroundName = NonNullable<
  VariantProps<typeof supportCardAvatarVariants>['surround']
>;
type SupportCardPresenceName = NonNullable<
  VariantProps<typeof supportCardAvatarVariants>['presence']
>;

export type SupportCardAvatarVariant = SupportCardAvatarVariantName;
export type SupportCardSurround = SupportCardSurroundName;
export type SupportCardPresenceState = SupportCardPresenceName;

/** The frame's size. */
export const SupportCardAvatarVariant = {
  /** The large card's. */
  LARGE: 'large',
  /** The compact card's. */
  COMPACT: 'compact',
} as const satisfies Record<string, SupportCardAvatarVariantName>;

/** The ground standing behind the card, which the frame's border paints. */
export const SupportCardSurround = {
  /** The plain ground. */
  BASE: 'base',
  /** The weighted ground. */
  INTENSE: 'intense',
  /** No border. */
  UNFRAMED: 'unframed',
} as const satisfies Record<string, SupportCardSurroundName>;

/** Whether the advisor is there. */
export const SupportCardPresenceState = {
  /** Within business hours. */
  ONLINE: 'online',
  /** Outside them. */
  OFFLINE: 'offline',
} as const satisfies Record<string, SupportCardPresenceName>;

export type SupportCardAvatarProps = ComponentProps<'div'> & {
  readonly variant: SupportCardAvatarVariant;
  readonly surround: SupportCardSurround;
  readonly presence: SupportCardPresenceState;
};

/**
 * The photograph's frame.
 *
 * The frame and the photograph are two parts rather than one: a brand with no
 * portrait of its advisor yet still has the frame, and stands its own mark in
 * it. What else the frame holds — a presence badge pinned to its corner — is
 * composed for the same reason.
 */
export const SupportCardAvatar = ({
  className,
  variant,
  surround,
  presence,
  ...props
}: SupportCardAvatarProps) => (
  <div
    data-slot="support-card-avatar"
    className={clsxm(supportCardAvatarVariants({ variant, surround, presence }), className)}
    {...props}
  />
);

/**
 * `className` alone rather than a native prop spread, for the reason
 * `../../internal/framedMedia.tsx` gives about media parts: what this renders
 * is the image seam, whose surface is the portable `VsImageProps` contract
 * rather than an `<img>`'s.
 */
export type SupportCardPhotoProps = FramedMediaProps;

/**
 * The advisor's photograph, filling whatever frame it is composed into.
 *
 * Renders through the `Image` seam, so a host that aliases the specifier gets
 * its resizing, `srcset` and lazy behaviour, and one that does not resolves the
 * portable plain `<img>`.
 */
export const SupportCardPhoto = ({ className, ...props }: SupportCardPhotoProps) => (
  <Image {...props} className={clsxm('h-full w-full rounded-full object-cover', className)} />
);

/* ── the presence dot ─────────────────────────────────────────────────── */

/**
 * The mark that says whether anyone is there.
 *
 * Three places rather than three components: beside the name, where it reads as
 * punctuation on the line, or pinned to the photograph's frame at its head or
 * at its foot, where it reads as a badge. All three say the same thing, so
 * `presence` is one axis across them.
 */
const supportCardPresenceVariants = cva('rounded-full', {
  variants: {
    variant: {
      /** Punctuation on the line the name sits on. */
      inline: 'size-2',
      /** A badge on the frame it is pinned to, cut out of the card's own ground. */
      pinned: 'ring-background absolute top-0 left-0 z-10 size-4 ring-4',
      /** The same badge at the foot of that frame, where a portrait's chin is. */
      pinnedFoot: 'ring-background absolute bottom-0 left-0 z-10 size-3 ring-2',
    },
    presence: {
      /** Within business hours. */
      online: 'bg-support-online',
      /** Outside them. */
      offline: 'bg-support-offline',
    },
  },
});

type SupportCardPresenceVariantName = NonNullable<
  VariantProps<typeof supportCardPresenceVariants>['variant']
>;

export type SupportCardPresenceVariant = SupportCardPresenceVariantName;

/** Where the mark stands. */
export const SupportCardPresenceVariant = {
  /** Beside the name. */
  INLINE: 'inline',
  /** On the photograph, at its head. */
  PINNED: 'pinned',
  /** On the photograph, at its foot. */
  PINNED_FOOT: 'pinnedFoot',
} as const satisfies Record<string, SupportCardPresenceVariantName>;

/** Required and closed — not `VariantProps`, which makes every axis optional. */
export type SupportCardPresenceProps = ComponentProps<'span'> & {
  readonly variant: SupportCardPresenceVariant;
  readonly presence: SupportCardPresenceState;
};

/** The presence mark. */
export const SupportCardPresence = ({
  className,
  variant,
  presence,
  ...props
}: SupportCardPresenceProps) => (
  <span
    data-slot="support-card-presence"
    className={clsxm(supportCardPresenceVariants({ variant, presence }), className)}
    {...props}
  />
);

/* ── what the card says ───────────────────────────────────────────────── */

export type SupportCardNameProps = ComponentProps<'span'>;

/**
 * Who answers, in the brand's display face.
 *
 * A neutral element, never a heading: a card that repeats down a listing rail
 * and again in a checkout column would contribute an advisor's name to the
 * page's outline every time it appeared.
 */
export const SupportCardName = ({ className, ...props }: SupportCardNameProps) => (
  <span
    data-slot="support-card-name"
    className={clsxm('font-heading text-xl tracking-tight', className)}
    {...props}
  />
);

export type SupportCardTextProps = ComponentProps<'span'>;

/** What the card calls itself, set small and quiet over the ways to reach it. */
export const SupportCardText = ({ className, ...props }: SupportCardTextProps) => (
  <span
    data-slot="support-card-text"
    className={clsxm('text-foreground-muted text-xs font-semibold uppercase', className)}
    {...props}
  />
);

/**
 * When that name is answered.
 *
 * The large card sets its hours as a footnote under everything else; the
 * compact one sets them at reading size directly under the name, because there
 * they are the second thing the row says rather than the last.
 */
const supportCardHoursVariants = cva('tracking-tight', {
  variants: {
    variant: {
      /** A footnote under the ways to reach the advisor. */
      large: 'text-foreground-muted text-xs',
      /** A line under the name, at reading size. */
      compact: 'text-foreground',
    },
  },
});

type SupportCardHoursVariantName = NonNullable<
  VariantProps<typeof supportCardHoursVariants>['variant']
>;

export type SupportCardHoursVariant = SupportCardHoursVariantName;

/** How loudly the hours read. */
export const SupportCardHoursVariant = {
  /** A footnote. */
  LARGE: 'large',
  /** A line at reading size. */
  COMPACT: 'compact',
} as const satisfies Record<string, SupportCardHoursVariantName>;

/** Required and closed — not `VariantProps`, which makes it optional. */
export type SupportCardHoursProps = ComponentProps<'span'> & {
  readonly variant: SupportCardHoursVariant;
};

/** The opening hours. */
export const SupportCardHours = ({ className, variant, ...props }: SupportCardHoursProps) => (
  <span
    data-slot="support-card-hours"
    className={clsxm(supportCardHoursVariants({ variant }), className)}
    {...props}
  />
);

/* ── the ways to reach the advisor ────────────────────────────────────── */

/**
 * How open the contact row stands.
 *
 * The card's own rows sit tight under a name already set larger than them; a
 * checkout step's single row is the only thing on it and is opened up to the
 * type around it. The row, its mark and its text answer to this one axis
 * together — a row that spaced itself while its mark stayed small would read as
 * a mistake rather than as a size.
 */
export const supportCardContactVariants = cva('flex items-center gap-1', {
  variants: {
    variant: {
      /** The card's own row. */
      classic: '',
      /** Opened up to the type around it, where the row stands alone. */
      checkout: 'gap-2',
      /** The opt-out: a tenant spacing the row itself. */
      unstyled: '',
    },
  },
});

type SupportCardContactVariantName = NonNullable<
  VariantProps<typeof supportCardContactVariants>['variant']
>;

export type SupportCardContactVariant = SupportCardContactVariantName;

/** `satisfies` binds these to the recipe above, so the two cannot drift. */
export const SupportCardContactVariant = {
  /** The card's own row. */
  CLASSIC: 'classic',
  /** The opened-up row. */
  CHECKOUT: 'checkout',
  /** The opt-out. */
  UNSTYLED: 'unstyled',
} as const satisfies Record<string, SupportCardContactVariantName>;

/** Required and closed — not `VariantProps`, which makes it optional. */
export type SupportCardContactVariantProps = {
  readonly variant: SupportCardContactVariant;
};

export type SupportCardContactProps = ComponentProps<'a'> & SupportCardContactVariantProps;

/**
 * One way to reach them — a telephone number, an address to write to.
 *
 * One part rather than one per channel: a `tel:` link and a `mailto:` link are
 * a mark and a string on the same row, and which of the two it is lives in the
 * `href` the tenant hands it. The mark itself is composed as a child, because
 * which drawing stands for a telephone is the tenant's iconography.
 */
export const SupportCardContact = ({ className, variant, ...props }: SupportCardContactProps) => (
  <a
    data-slot="support-card-contact"
    className={clsxm(supportCardContactVariants({ variant }), className)}
    {...props}
  />
);

/**
 * The mark on the row, sized with it.
 *
 * Which drawing stands for a telephone is the tenant's, so the icon arrives
 * through `render`; how quiet it is and how big it reads beside the row's type
 * is the section's.
 */
export const supportCardContactMarkVariants = cva('text-foreground/50 shrink-0', {
  variants: {
    variant: {
      /** At the drawing's own size. */
      classic: '',
      /** Sized to the larger type it stands beside. */
      checkout: 'size-4.5',
      /** The opt-out: a tenant sizing the mark itself. */
      unstyled: '',
    },
  },
});

export type SupportCardContactMarkProps = ComponentProps<'div'> &
  SupportCardContactVariantProps & {
    /** The icon to paint instead of the neutral `<div>`. */
    readonly render?: RenderElement;
  };

/** The row's mark. */
export const SupportCardContactMark = ({
  className,
  variant,
  render,
  ...props
}: SupportCardContactMarkProps) => (
  <RenderedPart
    dataSlot="support-card-contact-mark"
    className={clsxm(supportCardContactMarkVariants({ variant }), className)}
    render={render}
    {...props}
  />
);

/**
 * The number or the address itself.
 *
 * The large card sets it in bold at a fixed leading, because it is the loudest
 * thing in a column of small type; the compact one lets it read at the row's
 * own size, beside a name already set larger than it.
 */
const supportCardContactTextVariants = cva('tracking-tight', {
  variants: {
    variant: {
      /** Bold, at the leading the column's rhythm is built on. */
      large: 'text-sm/[19px] font-semibold',
      /** At the row's own size. */
      compact: '',
      /** At reading size, which is what a number mid-order is. */
      reading: 'text-base',
    },
  },
});

type SupportCardContactTextVariantName = NonNullable<
  VariantProps<typeof supportCardContactTextVariants>['variant']
>;

export type SupportCardContactTextVariant = SupportCardContactTextVariantName;

/** How loudly the contact reads. */
export const SupportCardContactTextVariant = {
  /** Bold, at a fixed leading. */
  LARGE: 'large',
  /** At the row's own size. */
  COMPACT: 'compact',
  /** At reading size. */
  READING: 'reading',
} as const satisfies Record<string, SupportCardContactTextVariantName>;

/** Required and closed — not `VariantProps`, which makes it optional. */
export type SupportCardContactTextProps = ComponentProps<'span'> & {
  readonly variant: SupportCardContactTextVariant;
};

/** What a shopper reads and dials. */
export const SupportCardContactText = ({
  className,
  variant,
  ...props
}: SupportCardContactTextProps) => (
  <span
    data-slot="support-card-contact-text"
    className={clsxm(supportCardContactTextVariants({ variant }), className)}
    {...props}
  />
);

/* ── the chat control ─────────────────────────────────────────────────── */

/**
 * The control that opens a conversation.
 *
 * Its arm is the CHAT's presence, which is not the advisor's: a shop can man a
 * telephone within business hours and a chat window outside them, and the card
 * shows both signals at once. The control is offered either way — what changes
 * is the colour it is drawn in and the words the tenant puts in it.
 *
 * Composes `@vs-shop/ui`'s `Button`, whose border it drops: the card's chat
 * control is a filled block of status colour rather than an outlined control.
 */
const supportCardChatButtonVariants = cva('border-none whitespace-nowrap', {
  variants: {
    presence: {
      /** Someone is at the other end now. */
      online: 'bg-support-online',
      /** The window is open but unmanned. */
      offline: 'bg-support-offline',
    },
  },
});

/** Required and closed — not `VariantProps`, which makes it optional. */
export type SupportCardChatButtonProps = ButtonProps & {
  readonly presence: SupportCardPresenceState;
};

/** The chat control. */
export const SupportCardChatButton = ({
  className,
  presence,
  ...props
}: SupportCardChatButtonProps) => (
  <Button
    data-slot="support-card-chat-button"
    className={clsxm(supportCardChatButtonVariants({ presence }), className)}
    {...props}
  />
);
